import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { euro } from "@/cart";

const API = process.env.REACT_APP_BACKEND_URL;
const STATUSES = ["pagato", "spedito", "consegnato", "annullato"];
const CLS = { pagato: "border-red-600 text-red-500", spedito: "border-amber-700 text-amber-500", consegnato: "border-emerald-800 text-emerald-500", annullato: "border-neutral-700 text-neutral-500", rimborsato: "border-neutral-700 text-neutral-500" };
const fmt = (iso) => new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });

export default function OrdersAdmin({ headers, onUnauthorized }) {
  const [orders, setOrders] = useState(null);
  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/admin/orders`, { headers: headers() });
      setOrders(data);
    } catch (err) {
      if (err.response?.status === 401) onUnauthorized();
      else toast("Impossibile caricare gli ordini");
    }
  }, [headers, onUnauthorized]);
  useEffect(() => {
    load();
  }, [load]);

  const [tracking, setTracking] = useState({});

  const setStatus = async (o, status) => {
    if (status === "spedito" && !o.shipped_notified_at) {
      const code = tracking[o.id] ?? "";
      if (!window.confirm(`Segnare come spedito e avvisare ${o.customer?.email} via email${code ? ` (tracking ${code})` : ""}?`)) return;
    }
    try {
      const { data } = await axios.patch(`${API}/api/admin/orders/${o.id}`, { status, tracking: tracking[o.id] ?? "" }, { headers: headers() });
      setOrders((list) => list.map((x) => (x.id === o.id ? data : x)));
      toast(status === "spedito" && !o.shipped_notified_at ? "Ordine spedito: email inviata al cliente" : "Stato ordine aggiornato", { description: `#${o.id.slice(0, 8)} → ${status}` });
    } catch {
      toast("Aggiornamento non riuscito");
    }
  };

  const addr = (a) => (a ? [a.line1, a.line2, `${a.postal_code || ""} ${a.city || ""}`.trim(), a.state, a.country].filter(Boolean).join(", ") : "—");

  return (
    <div data-testid="admin-orders-list" className="space-y-3">
      {orders?.length === 0 && <p className="border border-dashed border-neutral-800 p-10 text-center text-sm text-neutral-500">Nessun ordine ancora. Appariranno qui appena qualcuno paga dal negozio.</p>}
      {orders?.map((o) => (
        <article key={o.id} data-testid="admin-order-card" className="border border-neutral-800 bg-[#0D0D0D] p-5">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              <p className="font-mono-tac text-[10px] uppercase tracking-[0.3em] text-neutral-500">{fmt(o.created_at)} · #{o.id.slice(0, 8)}</p>
              <ul className="mt-2 space-y-0.5 text-sm text-neutral-200">
                {o.items.map((i, k) => (
                  <li key={k}>{i.name}{i.size ? ` · ${i.size}` : ""} × {i.quantity} <span className="text-neutral-500">— {euro(i.unit_cents * i.quantity)}</span></li>
                ))}
              </ul>
              <p className="mt-2 text-sm font-bold text-neutral-100">Totale {euro(o.amount_total_cents ?? o.total_cents)} <span className="font-normal text-neutral-500">(spedizione {euro(o.shipping_cents)}{o.discount_cents ? ` · sconto ${euro(o.discount_cents)}${o.promo_code ? ` con ${o.promo_code}` : ""}` : ""})</span></p>
              <p className="mt-3 text-sm text-neutral-300">{o.customer?.name} · <a className="text-red-500 hover:underline" href={`mailto:${o.customer?.email}`}>{o.customer?.email}</a>{o.customer?.phone && <span className="text-neutral-400"> · {o.customer.phone}</span>}</p>
              <p className="mt-1 text-sm text-neutral-400">{addr(o.shipping_address)}</p>
            </div>
            <div className="flex shrink-0 flex-col items-stretch gap-2 sm:items-end">
            <select data-testid="admin-order-status" value={STATUSES.includes(o.status) ? o.status : "annullato"} onChange={(e) => setStatus(o, e.target.value)} className={`h-9 shrink-0 border bg-black/40 px-2 font-mono-tac text-[10px] uppercase tracking-[0.2em] focus:outline-none ${CLS[o.status] || CLS.annullato}`}>
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <input
              data-testid="admin-order-tracking"
              value={tracking[o.id] ?? o.tracking ?? ""}
              onChange={(e) => setTracking({ ...tracking, [o.id]: e.target.value })}
              placeholder="Codice tracking (opz.)"
              className="h-9 w-48 border border-neutral-800 bg-black/40 px-2 font-mono-tac text-[11px] text-neutral-100 placeholder:text-neutral-600 focus:border-red-700 focus:outline-none"
            />
            <p data-testid="admin-order-shipped-info" className="font-mono-tac text-[9px] uppercase tracking-[0.2em] text-neutral-600">
              {o.shipped_notified_at ? `Cliente avvisato il ${fmt(o.shipped_notified_at)}` : "Cliente non ancora avvisato"}
            </p>
            </div>
          </div>
        </article>
      ))}
    </div>
  );
}
